import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  draftPublicLine,
  fetchRecapConfig,
  refreshFromX,
  saveDisplayName,
  updateDoorPublicLine,
  updateDoorSettings,
  updateRecapConfig,
  type DoorSettings,
  type DoorSettingsInput,
  type DoorSettingsResult,
  type FieldIssue,
  type Me,
  type RecapConfig,
  type RecapConfigInput,
} from "../../lib/api";
import { AI_DRAFT_LABEL } from "@nob/schemas";
import { celebrate } from "../../lib/celebrate";
import { loadDoor } from "../../lib/draft";
import {
  blockedQuery,
  currentRulesQuery,
  doorQuery,
  doorSettingsQuery,
  meQuery,
  planQuery,
  qk,
} from "../../lib/queries";
import { useSessionExpired } from "../../lib/session";
import { LinkButton, SecondaryButton } from "../ui/button";
import { isPastDue, PAST_DUE_WALL_LINE, UpdateCardButton } from "../plan/usage-notice";
import { CopyButton } from "../ui/copy-button";
import { SignOutButton, useLeave, accountKey, whoami } from "../ui/account";
import { PersonFace } from "../shell/mascot";
import { useShellUi, type SettingsFocus } from "../shell/state";
import { Group, PLATE, Row } from "../shell/surfaces";
import { RecapConfigPanel } from "../recap/config";
import { RulesPanel } from "../rules/rules-panel";
import { ContactEmailSection } from "./contact-email";
import {
  BIO_REFUSED_COPY,
  BIO_TOO_LARGE_COPY,
  DoorSettingsForm,
  SAVED_ACK,
  SAVE_LABEL,
} from "./door-settings";
import { BLOCKED_TITLE, BlockedListDialog } from "./blocked-list";
import { DOOR_BACK_UP, REOPEN_QUESTION } from "./reopen";
import { TakedownControl, type TakedownOutcome } from "./takedown";

/**
 * Your door, as a page of the settings panel.
 *
 * This WAS the `/you` room, and almost none of it changed: the same mirror, the
 * same four settings, the same asymmetric pair of door controls, the same
 * ceremony. What changed is where it stands. A profile room made sense when the
 * app was a rail of rooms; in a messenger shell the profile is the footer you
 * are always looking at, and the things it used to hold are a panel that slides
 * over the conversation rather than a place you navigate away to.
 *
 * THERE IS NO VISIBILITY CONTROL AND THERE CANNOT BE ONE (ruling R-0825-1,
 * 2026-08-25). Every door is public and searchable, the way every X account is.
 * The select that used to stand at the top of this list, the `directory_listed`
 * column behind it and the whole "private = unsearchable" rule are deleted, not
 * defaulted-on.
 *
 * The room's other contents went where they belong rather than following the
 * door here: appearance to Settings -> Look, the plan line to the Billing
 * panel, and identity and sign-out to Settings -> Account. Each one is a page
 * instead of a paragraph. The channels line led to a screen that ruling
 * R-0908-3 deleted from the interface; nothing here replaces it.
 *
 * WHAT IS STILL DELIBERATELY NOT HERE: the balance. Money has a panel, and a
 * summary of it in a second place is a number that can disagree with itself.
 *
 * ALSO NOT HERE, AND NEVER WILL BE: a field for the handle. X owns the address
 * (PRD R10, E-91) - a door is a view of an X identity and Nob runs no namespace
 * of its own. Refresh, which copies what X says now, is the only honest verb for
 * it, and it lives on the Account page.
 *
 * THE NAME IS NO LONGER ONE OF THOSE STRINGS (ruling R-0915-3). It is seeded
 * from X and edited on the Account page, beside the picture and the address it
 * belongs with; from the moment it is edited, X stops being copied over it.
 */

/** How long an acknowledgement stays on screen. Long enough to read, once. */
const ACK_MS = 2000;

export const REFRESH_LABEL = "Refresh from X";
/**
 * THE NAME, AND WHERE IT CAME FROM (ruling R-0915-3).
 *
 * The label says whose name it is, because until this ruling it was X's and the
 * plate said so. The help line under it is the whole of the rule in one
 * sentence, and it changes once: while the name is still X's it says X is being
 * followed, and after the owner writes one it says X is not.
 */
export const NAME_LABEL = "Your name";
export const NAME_FOLLOWING_X = "Seeded from X, and it follows X until you change it here.";
export const NAME_YOURS = "Yours. Refreshing from X no longer changes it.";
export const NAME_SAVE_LABEL = "Save";
export const NAME_SAVED_ACK = "Saved.";
/** Said only where the server refused without a sentence of its own. */
export const NAME_UNREACHABLE = "That did not save. Try again.";
export const REFRESH_ACK = "Refreshed.";
export const REFRESH_FAILED = "X did not answer. Try again later.";
export const REOPEN_LABEL = "Put the door back up";
/**
 * THE DOOR'S PUBLIC LINE (ruling R-0910-10), and why it has a field of its own.
 *
 * The House Rules are private without exception now — no section of that
 * document reaches the plate, the card, the image or the API — so the thing a
 * stranger reads under "What gets through" is this line and nothing else. The
 * label says WHERE IT GOES and WHO READS IT, because the whole failure this
 * replaces was an owner writing an honest instruction to their doorman and
 * finding it printed on their door.
 */
export const PUBLIC_LINE_LABEL = "What gets through, as strangers read it";
export const PUBLIC_LINE_HELP = "Shown on your door. Your House Rules stay private.";
/** An empty field is a door with no such block: the consequence, not the absence. */
export const PUBLIC_LINE_EMPTY = "Nothing written yet — your door shows no such block.";
export const PUBLIC_LINE_TOO_LARGE_COPY = "That is longer than a line on a door can be.";
export const PUBLIC_LINE_REFUSED_COPY = "Nob won’t put that on your door. Say it differently.";
export const PUBLIC_LINE_UNREACHABLE = "That did not save. Try again.";
/** The offer, said as what it does. It reads the rules; it does not publish them. */
export const DRAFT_LINE_LABEL = "Ask Nob to write it from your rules";
export const DRAFT_LINE_HELP = "It fills the box. Nothing is saved until you press Save.";
export const DRAFTING_LINE_LABEL = "Reading your rules…";
/**
 * THE LABEL ON ANYTHING NOB WROTE (EU AI Act Art. 50(2); ruling R-0914-1 (11)).
 *
 * It replaces "Drafted. Edit it, then save." and the difference is the whole
 * point: the old line said what happened, this one says WHO — a line produced by
 * a model, sitting in an editable box on somebody's own door, is content a
 * reader could reasonably take for their own words unless something says
 * otherwise. The bio and rules proposals in the owner's thread carry the same
 * sentence, which is why it is authored in `@nob/schemas` and only re-exported
 * here for the tests that already read this module's copy.
 */
export { AI_DRAFT_LABEL };
/** What a refused draft says, by the server's reason. Each one says what to do next. */
export const DRAFT_LINE_FAILED: Record<string, string> = {
  NO_RULES: "Your rules do not say enough yet to write from. Write the line yourself.",
  INPUT_REFUSED: "What came back would not go on a door. Try again, or write your own.",
  PLAN_EXHAUSTED: "Not enough left this period to write it for you.",
  PAYMENT_RISK_SUSPENDED:
    "Paid features are suspended while a payment dispute is open, so Nob is not doing AI work.",
};
export const DRAFT_LINE_UNREACHABLE = "Nob could not write it just now. Try that again.";
/**
 * The nudge a Free door gets instead. It names the LACK, not the price (FR-9):
 * a free door performs no AI work, so this is work it cannot do.
 */
export const DRAFT_LINE_LOCKED =
  "Writing it from your rules is AI work, which a free door does not do.";
/** Where that nudge sends them. */
export const DRAFT_LINE_UPGRADE = "See plans";
/** The way to the ONE share sheet (ruling: no second, inline share surface). */
export const SHARE_DOOR_LABEL = "Share your door";
export const SHARE_DOOR_NOTE =
  "Ready-made lines for a bio, a DM, a post or a signature — and the cards that go with them.";
/** What is true while the door is down, said where the way back up is offered. */
export const PAUSED_NOTE =
  "Knockers are being turned away at the door. The address still resolves, and nothing was deleted.";

/**
 * The canonical address, when the created door's own URL is not to hand.
 *
 * Exported for Home's console, which states the same address in its door
 * summary. Two rooms writing an address two ways is how a product ends up with
 * two addresses; there is one function, so there is one answer.
 */
export function canonicalUrl(handle: string): string {
  return `https://nob.me/@${handle}`;
}

/**
 * The `public_url` the server minted when the door was opened, if this browser
 * still has it and it is THIS person's. The canonical form otherwise: a copied
 * address must be right, and an address belonging to whoever used this browser
 * last would be worse than a computed one.
 */
function addressToCopy(handle: string): string {
  const stored = loadDoor();
  return stored && stored.door.handle === handle && stored.door.public_url
    ? stored.door.public_url
    : canonicalUrl(handle);
}

export function DoorPage({ focus }: { focus?: SettingsFocus }) {
  const queryClient = useQueryClient();
  const expireSession = useSessionExpired();
  const { data: me } = useQuery(meQuery());

  /**
   * The handle a door of theirs would be at, which is the whole answer: X is
   * the only way in (ruling R-0826-1), so a session is an X identity and the
   * handle IS the address. `null` here is a session that has not loaded, not a
   * person who may not own a door — there is no such person any more.
   */
  const handle = me?.xHandle ?? null;

  /**
   * The door as a STRANGER is served it (`GET /door/:handle/public`) — the same
   * card the public page renders, read here on purpose: a mirror that showed the
   * owner a privately-computed version of themselves would not be a mirror.
   *
   * It answers 409 DOOR_CLOSED for a PAUSED door, which is exactly right for a
   * stranger and useless for an owner — so the state line below reads the
   * owner's own settings, and this query stays what it is: the mirror.
   */
  const publicDoor = useQuery({ ...doorQuery(handle ?? ""), enabled: handle !== null });

  /** The owner's own four settings (2026-08-03). */
  const settingsQuery = useQuery({ ...doorSettingsQuery(), enabled: handle !== null });
  const settings = settingsQuery.data ?? null;

  /**
   * Whether this plan does AI WORK AT ALL — `metered`, which is false on Free
   * (ruling C10) and true on every paid tier and every trial. Read here rather
   * than reasoned about from the tier name: the tiers move and the flag is the
   * server's own answer to exactly this question.
   */
  const { data: plan } = useQuery(planQuery());
  const canDraftLine = plan?.plan.metered === true;
  /** Past due: an exhausted refusal is the past-due wall's (ruling R-1006-3). */
  const pastDue = isPastDue(plan?.plan);

  /** Delivery, moved here from the briefing. Read lazily, on first open. */
  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState<RecapConfig | null>(null);
  const [configWarning, setConfigWarning] = useState<string | null>(null);
  const [configBusy, setConfigBusy] = useState(false);
  const configLoaded = useRef(false);

  /** The settings form's own network state: in flight, refused, acknowledged. */
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsIssues, setSettingsIssues] = useState<FieldIssue[]>([]);
  const [savedAck, setSavedAck] = useState(false);

  const expired = useRef(expireSession);
  expired.current = expireSession;

  /** Every acknowledgement timer this room owns, cleared on the way out. */
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const t of pending) clearTimeout(t);
    };
  }, []);
  const acknowledge = useCallback((show: (on: boolean) => void) => {
    show(true);
    timers.current.push(setTimeout(() => show(false), ACK_MS));
  }, []);

  const toggleConfig = useCallback(async () => {
    setConfigOpen((o) => !o);
    if (configLoaded.current) return;
    configLoaded.current = true;
    const r = await fetchRecapConfig();
    if (!r.ok) {
      if (r.code === "UNAUTHENTICATED") expired.current();
      else configLoaded.current = false; // a failed read may be tried again on reopen
      return;
    }
    setConfig(r.config);
    setConfigWarning(r.warning);
  }, []);

  const patchConfig = useCallback(async (input: RecapConfigInput) => {
    // Optimistic: the change is never blocked (FR-7.2), so the control moves at once.
    setConfig((prev) => (prev ? { ...prev, ...input } : prev));
    setConfigBusy(true);
    const r = await updateRecapConfig(input);
    setConfigBusy(false);
    if (!r.ok) {
      if (r.code === "UNAUTHENTICATED") expired.current();
      return;
    }
    setConfig(r.config);
    setConfigWarning(r.warning);
  }, []);

  /**
   * One write for the form and for the pause button, because they PATCH the same
   * endpoint. The cached settings are replaced with the server's answer and the
   * PUBLIC card is invalidated too: the price and the status both feed the
   * mirror, so a door that just paused must not keep showing a stranger's view
   * from before it did.
   */
  const saveSettings = useCallback(
    async (input: DoorSettingsInput): Promise<DoorSettingsResult> => {
      setSettingsBusy(true);
      const r = await updateDoorSettings(input);
      setSettingsBusy(false);
      if (!r.ok) {
        if (r.code === "UNAUTHENTICATED") expired.current();
        // The bio's two content refusals come back as codes with no sentence
        // attached — the server refused what the text SAYS (FR-3.8.1), and the
        // room says so in its own voice, beside the box that earned it.
        setSettingsIssues(
          r.code === "BIO_TOO_LARGE"
            ? [{ field: "bio", message: BIO_TOO_LARGE_COPY }]
            : r.code === "INPUT_REFUSED"
              ? [{ field: "bio", message: BIO_REFUSED_COPY }]
              : r.issues,
        );
        return r;
      }
      setSettingsIssues([]);
      /**
       * A door going back up is the fourth moment worth marking. Taking one
       * down never is: that is a ceremony, and a ceremony followed by confetti
       * would be the product cheering at somebody's decision to close.
       */
      // The words are `reopen.tsx`'s, because the confirm the four "Paused"
      // markers open raises the same event — two copies of one celebration is
      // how the two come to say different things about the same door.
      if (input.status === "live" && r.settings.status === "live") celebrate(DOOR_BACK_UP);
      queryClient.setQueryData<DoorSettings>(qk.doorSettings(), r.settings);
      void queryClient.invalidateQueries({ queryKey: qk.doorSettings() });
      if (handle !== null) void queryClient.invalidateQueries({ queryKey: qk.door(handle) });
      acknowledge(setSavedAck);
      return r;
    },
    [acknowledge, handle, queryClient],
  );

  /**
   * The takedown, as the ceremony needs it answered: it took, the server did
   * not accept the confirmation, or nothing was reached. The modal stays open
   * for the last two — a dialog that closes on a failed takedown would be the
   * screen claiming the door is down when it is up.
   */
  const takeDown = useCallback(
    async (confirmHandle: string): Promise<TakedownOutcome> => {
      const r = await saveSettings({ status: "paused", confirm_handle: confirmHandle });
      if (r.ok) return "ok";
      return r.code === "CONFIRM_REQUIRED" ? "refused" : "unreachable";
    },
    [saveSettings],
  );

  // The panel only opens for a session, so this is the frame before the cache
  // answers.
  if (!me) return <div className="min-h-[40vh]" aria-hidden />;

  /**
   * Two independent server truths, either of which proves a door: the public
   * card resolved, or the settings read. Either is enough, and neither has to
   * wait for the other.
   */
  const hasDoor = handle !== null && (publicDoor.data !== undefined || settings !== null);

  // A session with no handle is a session still landing, never a person who may
  // not own a door: X is the only way in and every identity carries one.
  if (handle === null) return <div className="min-h-[40vh]" aria-hidden />;

  return (
    <>
      {hasDoor ? (
        <YourDoor
          handle={handle}
          focus={focus}
          settings={settings}
          publicCardResolved={publicDoor.data !== undefined}
          settingsBusy={settingsBusy}
          settingsIssues={settingsIssues}
          savedAck={savedAck}
          onSaveSettings={(input) => void saveSettings(input)}
          canDraftLine={canDraftLine}
          pastDue={pastDue}
          configOpen={configOpen}
          config={config}
          configWarning={configWarning}
          configBusy={configBusy}
          onToggleConfig={() => void toggleConfig()}
          onCap={(cap) => void patchConfig({ cap })}
          onCadence={(cadence) => void patchConfig({ cadence })}
          onHour={(hour) => void patchConfig({ hour })}
          onTakeDown={takeDown}
        />
      ) : null}
    </>
  );
}

/**
 * Who you are, where Nob writes to you, and the way out.
 *
 * The identity block is unchanged from the room this came from, including the
 * one line explaining why there is nothing here to edit.
 */
export function AccountPage() {
  const queryClient = useQueryClient();
  const expireSession = useSessionExpired();
  const { data: me } = useQuery(meQuery());
  const { leave, leaving } = useLeave();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const expired = useRef(expireSession);
  expired.current = expireSession;
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const t of pending) clearTimeout(t);
    };
  }, []);

  const refresh = useCallback(async () => {
    setRefreshError(null);
    setRefreshing(true);
    const r = await refreshFromX();
    setRefreshing(false);
    if (!r.ok) {
      if (r.code === "UNAUTHENTICATED") return expired.current();
      return setRefreshError(REFRESH_FAILED);
    }
    void queryClient.invalidateQueries({ queryKey: qk.me() });
    void queryClient.invalidateQueries({ queryKey: qk.doorSettings() });
    void queryClient.invalidateQueries({ queryKey: qk.door(r.identity.handle) });
    setRefreshNote(REFRESH_ACK);
    timers.current.push(setTimeout(() => setRefreshNote(null), ACK_MS));
  }, [queryClient]);

  if (!me) return <div className="min-h-[40vh]" aria-hidden />;

  return (
    <>
      <Group>
        <Identity
          me={me}
          refreshing={refreshing}
          note={refreshNote}
          error={refreshError}
          onRefresh={() => void refresh()}
        />
      </Group>
      {me.xHandle !== null && <ContactEmailSection me={me} />}
      <Group>
        <div className={PLATE}>
          <SignOutButton leaving={leaving} onLeave={leave} />
        </div>
      </Group>
    </>
  );
}

/**
 * Who this session belongs to, at the size a profile states it — the picture,
 * the address, and the one string on it the owner may write.
 *
 * THE PICTURE IS THEIR OWN (ruling R-0915-3). It used to be a monogram here
 * while every other surface in the product showed their face, which read as a
 * different account: the plate a stranger reads, the rail, the thread list and
 * this page are all the same person, so they all render the same cached image.
 * `/me` carries it now, so this page asks for nothing extra to draw it.
 */
function Identity({
  me,
  refreshing,
  note,
  error,
  onRefresh,
}: {
  me: Me;
  refreshing: boolean;
  note: string | null;
  error: string | null;
  onRefresh: () => void;
}) {
  const name = me.displayName || whoami(me);
  // There is no second sentence to pick between: X is the only way in (ruling
  // R-0826-1), so every session on this plate was opened with one.
  return (
    <>
      {/* Who, at the top of the plate: the portrait row. */}
      <header className={`${PLATE} flex items-center gap-3`}>
        <PersonFace name={name} handle={accountKey(me)} avatar={me.avatar} size="lg" />
        <div className="min-w-0">
          <h1 className="truncate text-[17px] font-semibold tracking-tight text-text">{name}</h1>
          <p className="mt-0.5 truncate font-mono text-[12px] text-faint">{whoami(me)}</p>
          <p className="mt-0.5 text-[11px] leading-snug text-fainter">Signed in with X</p>
        </div>
      </header>
      <NameField me={me} />
      {/* The address and the picture are still X's, and the button is a copy
          rather than an edit. Its own row under a hairline, because it is the
          last thing on this plate you can press. */}
      <div className="border-t border-border-soft px-3 py-3">
        <p className="flex flex-wrap items-baseline gap-x-3">
          <LinkButton type="button" onClick={onRefresh} disabled={refreshing}>
            {REFRESH_LABEL}
          </LinkButton>
          {note && (
            <span className="font-mono text-[11px] text-accent-ink/80" role="status">
              {note}
            </span>
          )}
        </p>
        {error && (
          <p className="mt-1.5 text-[13px] text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </>
  );
}

/**
 * The name, edited in place (ruling R-0915-3).
 *
 * ONE FIELD AND A SAVE, and no "Change" to press first: there is exactly one
 * writable string on this plate and hiding it behind a link would be a click
 * spent asking whether the screen meant it. The field holds what the account is
 * called; Save is dead until it holds something else.
 *
 * THE SERVER'S REFUSAL, VERBATIM. What a name may be — its length, its shape,
 * and what the guard thinks of it — is the API's rule, and a form that
 * paraphrased it would eventually paraphrase it wrong.
 */
function NameField({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(me.displayName);
  const [busy, setBusy] = useState(false);
  const [issue, setIssue] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const ack = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(ack.current), []);
  // What the row says is what the SERVER says, not what was typed into it: a
  // refresh from X or a save landing elsewhere must move this field too.
  useEffect(() => {
    setValue(me.displayName);
  }, [me.displayName]);

  const typed = value.trim();
  const save = useCallback(() => {
    if (busy || typed.length === 0 || typed === me.displayName) return;
    setBusy(true);
    setIssue(null);
    void saveDisplayName(typed).then((r) => {
      setBusy(false);
      if (!r.ok) return setIssue(r.message ?? NAME_UNREACHABLE);
      // Everything that renders a name reads `/me`; the door plate renders it
      // too, and a stranger's copy of it is the same string.
      void queryClient.invalidateQueries({ queryKey: qk.me() });
      if (me.xHandle) void queryClient.invalidateQueries({ queryKey: qk.door(me.xHandle) });
      setSaved(true);
      clearTimeout(ack.current);
      ack.current = setTimeout(() => setSaved(false), ACK_MS);
    });
  }, [busy, me.displayName, me.xHandle, queryClient, typed]);

  return (
    <form
      className="border-t border-border-soft px-3 py-3"
      // The SERVER owns what a name is, and this form prints its sentence.
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <label
        className="block text-[11px] uppercase tracking-[0.12em] text-faint"
        htmlFor="account-display-name"
      >
        {NAME_LABEL}
      </label>
      <div className="mt-1.5 flex flex-wrap items-center gap-3">
        <input
          id="account-display-name"
          type="text"
          maxLength={60}
          value={value}
          disabled={busy}
          aria-invalid={issue !== null}
          {...(issue ? { "aria-describedby": "account-display-name-error" } : {})}
          onChange={(e) => setValue(e.target.value)}
          className="h-9 w-full max-w-xs rounded-lg border border-border bg-bg/60 px-3 text-sm
                     text-text placeholder:text-fainter focus-visible:border-border-loud
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40
                     disabled:opacity-50 aria-[invalid=true]:border-danger/60"
        />
        <SecondaryButton
          type="submit"
          disabled={busy || typed.length === 0 || typed === me.displayName}
        >
          {NAME_SAVE_LABEL}
        </SecondaryButton>
        {saved && (
          <span className="font-mono text-[11px] text-accent-ink/80" role="status">
            {NAME_SAVED_ACK}
          </span>
        )}
      </div>
      {issue && (
        <p id="account-display-name-error" role="alert" className="mt-1.5 text-[13px] text-danger">
          {issue}
        </p>
      )}
      <p className="mt-1.5 text-[12px] leading-snug text-faint">
        {me.displayNameSetAt === null ? NAME_FOLLOWING_X : NAME_YOURS}
      </p>
    </form>
  );
}

type DoorProps = {
  handle: string;
  /** The CTA that opened this page meant one block of it. See `SettingsFocus`. */
  focus?: SettingsFocus;
  /** `null` while the owner's settings are still being read, or if they would not read. */
  settings: DoorSettings | null;
  /** The price the OWNER set, from their own settings read. The public card has
   * no figure to fall back on any more (owner decision 2026-08-21) — a price is
   * an owner's fact now, and this room is where it is stated. */
  /**
   * The PUBLIC card resolved. It stands in for the owner's own settings read
   * while that is still in flight, because that card exists only for a live
   * door — it used to be inferred from the door's price, and there is no price
   * any more (ruling C3).
   */
  publicCardResolved: boolean;
  settingsBusy: boolean;
  settingsIssues: FieldIssue[];
  savedAck: boolean;
  onSaveSettings: (input: DoorSettingsInput) => void;
  /** False on Free, where the door performs no AI work at all (ruling C10). */
  canDraftLine: boolean;
  /** True while the payment is failing (ruling R-1006-3). */
  pastDue: boolean;
  /** The ceremony's one call: paused, with the handle somebody typed out. */
  onTakeDown: (confirmHandle: string) => Promise<TakedownOutcome>;
  configOpen: boolean;
  config: RecapConfig | null;
  configWarning: string | null;
  configBusy: boolean;
  onToggleConfig: () => void;
  onCap: (cap: number) => void;
  onCadence: (cadence: string) => void;
  onHour: (hour: number) => void;
};

/**
 * The state line: what the door IS, and nothing about what it costs. A door has
 * no price (ruling C3) — there is no figure left to append here, and the line is
 * the status on its own.
 */
function stateLine(status: "live" | "paused"): string {
  return status === "live" ? "Live" : "Paused";
}

function YourDoor({
  handle,
  focus,
  settings,
  publicCardResolved,
  settingsBusy,
  settingsIssues,
  savedAck,
  onSaveSettings,
  canDraftLine,
  pastDue,
  onTakeDown,
  configOpen,
  config,
  configWarning,
  configBusy,
  onToggleConfig,
  onCap,
  onCadence,
  onHour,
}: DoorProps) {
  /**
   * The shell, read here rather than threaded down as two more callbacks: both
   * things this section hands off — the share sheet and the plans panel — are
   * surfaces the shell owns and opens from several places, and a prop would be a
   * second name for an opener that already has one.
   */
  const ui = useShellUi();
  // Live when the owner's own settings say so; live when they have not answered
  // but the PUBLIC card has, because that card exists only for a live door.
  const status = settings?.status ?? (publicCardResolved ? "live" : null);
  const paused = status === "paused";

  /**
   * Who this door refuses (FR-6.4). Read here so the ROW can print the count;
   * the dialog reads the same key, so the number and the list it opens are one
   * fact. `retry: false` and a dash when it has not answered — a count is not
   * something to guess at, and least of all this one.
   */
  const [blockedOpen, setBlockedOpen] = useState(false);
  const blocked = useQuery({ ...blockedQuery(), enabled: settings !== null, retry: false });
  const blockedCount = blocked.data?.length ?? null;

  /**
   * "PERSONALIZE YOUR HOUSE RULES" MEANS THE CONTROLS, NOT THE PAGE (owner item
   * 8, 2026-08-27).
   *
   * The CTA used to open this page and stop. The rules are its fifth block, and
   * the three buttons that change them are at the FOOT of that block, under the
   * whole document — so somebody who pressed a button naming the rules arrived
   * looking at their address and their bio, with no sign that the thing they
   * asked for was two screens further down.
   *
   * It scrolls to the CONTROLS rather than to the top of the block, because the
   * block opens with the document and the document is not what was asked for;
   * and it takes focus, which is the half a screen reader needs — a page that
   * scrolled somewhere the caret did not follow has moved nobody.
   *
   * The read is what the effect waits on: `RulesPanel` fetches the same cached
   * query, so this fires again on the render that first puts the controls in the
   * DOM rather than once, early, against an empty block.
   */
  const rulesBlock = useRef<HTMLDivElement>(null);
  const { data: rulesNow } = useQuery({ ...currentRulesQuery(), enabled: focus === "rules" });
  useEffect(() => {
    if (focus !== "rules") return;
    const controls = rulesBlock.current?.querySelector<HTMLElement>("[data-rules-controls]");
    if (!controls) return;
    controls.scrollIntoView?.({ block: "center" });
    controls.querySelector("button")?.focus();
  }, [focus, rulesNow]);
  /**
   * …AND "WHAT GETS THROUGH" MEANS THE PUBLIC LINE (ruling R-0925-2 (5)). The
   * rail prints the line under that heading, so its Edit lands on the field the
   * line is written in, scrolled into view with the caret in it.
   */
  useEffect(() => {
    if (focus !== "public-line") return;
    const field = document.getElementById("door-public-line");
    if (!field) return;
    field.scrollIntoView?.({ block: "center" });
    field.focus();
  }, [focus, settings]);

  return (
    /**
     * EVERY SECTION IS A PLATE (reference build, 2026-08-25).
     *
     * This page used to be one long column of prose ruled off by hairlines —
     * the shape it was born in, as a room at `/you`. In a 380px panel that
     * reads as a scroll rather than as settings, which is exactly the
     * complaint. So each thing the page governs now stands on its own `bg-bg`
     * card, inset from the panel's yellow, with rows contained inside it.
     * Nothing about WHAT is here changed, or in what order.
     */
    <>
      {/* 1. What the door IS: its address, its state, and the way to look at
             it from outside. Three readouts, one plate. */}
      <Group>
        <div className={`${PLATE} flex flex-wrap items-center gap-x-3 gap-y-2`}>
          <p className="min-w-0 font-mono text-[17px] font-medium leading-none tracking-tight text-accent-ink">
            <span className="text-faint">nob.me/</span>@{handle}
          </p>
          <CopyButton value={addressToCopy(handle)} label="Copy" />
        </div>

        {/* What the door is. Absent rather than guessed at when neither read has
            answered — a state is not a thing to invent.

            AND WHEN IT SAYS "PAUSED" IT IS A CONTROL (owner item 13). The whole
            row is the marker rather than the word inside it: `Row` is already a
            button when it is given an `onClick`, and a button around the word
            would be a button inside a button. The way back up still sits at the
            foot of this page as well — this is a second door onto it, in the
            place the state is actually read, not a second path. */}
        {status !== null && (
          <Row
            label="Door"
            value={<span data-door-state>{stateLine(status)}</span>}
            {...(paused
              ? { onClick: () => ui.openSheet("reopen"), ariaLabel: REOPEN_QUESTION }
              : {})}
          />
        )}

        {/* THE MIRROR, AND IT NOW ACTUALLY SHOWS THE POSTER (owner item 14,
            2026-08-26). It used to leave for the API's server-rendered page at
            `/@handle` — which redirects any authed session to the console, so
            the one person who could not see their own door was its owner. It
            goes to `/@handle?poster=1`, which on one origin is the api's door
            page; with a session it hands the flag to the SPA's own door
            address (`/?knock=…&poster=1`, ruling R-0926-1 (1)), which renders
            the signed-out poster for a session instead of bouncing it.

            A PAUSED DOOR IS PART OF THE POINT (item 29): the poster reads the
            same public card a stranger's browser reads, so a door that is down
            answers this link with the closed poster — the simulation asked for,
            with no second code path to keep honest. */}
        <a
          href={`/@${handle}?poster=1`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full items-center gap-3 border-b border-border-soft px-3 py-3
                     text-[13px] text-muted transition-colors duration-75 last:border-b-0
                     hover:bg-hover hover:text-text focus-visible:outline-none
                     focus-visible:ring-2 focus-visible:ring-focus/70"
        >
          See it as strangers do &rarr;
        </a>

        {/* HANDING IT OUT, ON THE SAME PLATE AS THE ADDRESS (owner item 15).
            It stood in a group of its own two sections down, which put the two
            halves of one act — here is your address, here is how to give it out
            — on opposite sides of the bio. ONE share surface for the whole
            product: this opens the same sheet the sidebar and the doorman open,
            rather than a second copy of the cards standing inline. Two share
            screens is how the snippets in one come to differ from the snippets
            in the other. */}
        <div className={PLATE}>
          <SecondaryButton type="button" onClick={() => ui.openSheet("share")}>
            {SHARE_DOOR_LABEL}
          </SecondaryButton>
          <p className="mt-2 text-[13px] leading-snug text-fainter">{SHARE_DOOR_NOTE}</p>
        </div>
      </Group>

      {/* 2. The one line on this page a stranger reads. One form, one Save.
             The plate carries no label of its own: the form's first field is
             "Your bio", which is the same words twice if the plate says them
             first. */}
      {settings !== null && (
        <Group>
          <div className={PLATE}>
            <DoorSettingsForm
              settings={settings}
              busy={settingsBusy}
              issues={settingsIssues}
              saved={savedAck}
              onSave={onSaveSettings}
            />
          </div>
          {/* Beneath the bio, because that is the order a stranger reads them
              in: who this is, then what this door wants. Its own plate and its
              own Save, because it is its own screened write — the settings
              PATCH does not carry it (ruling R-0910-10). */}
          <div className={PLATE}>
            <PublicLineForm
              handle={handle}
              initial={settings.public_line}
              canDraft={canDraftLine}
              pastDue={pastDue}
              onUpgrade={() => ui.openBilling("plans")}
            />
          </div>
        </Group>
      )}

      <Group title="Morning Nob">
        <Row
          label="Morning Nob delivery"
          value={configOpen ? "Hide" : "When and how many"}
          onClick={onToggleConfig}
          ariaLabel="Morning Nob delivery"
        />
        <div className={configOpen ? PLATE : "hidden"}>
          <RecapConfigPanel
            open={configOpen}
            config={config}
            warning={configWarning}
            busy={configBusy}
            onCap={onCap}
            onCadence={onCadence}
            onHour={onHour}
          />
        </div>
      </Group>

      {/* 4. The third thing that governs this door, and the largest: the
             document itself. It had a room of its own and no longer does —
             the House Rules are a property of the door, and they now sit on
             the door's page rather than at an address of their own. The
             editor, the diff, the history and the proposals are the same
             components they always were; only the wall around them moved.

             The plate carries NO title: what is inside already prints "House
             Rules — @handle" at the top of every one of its five views, and a
             label above it would be the same words twice. */}
      <Group>
        <div className="py-3" ref={rulesBlock}>
          <RulesPanel />
        </div>
      </Group>

      {/* 4a. Who this door refuses (FR-6.4), directly above the takedown.
              The two belong together and in this order: both are the door
              closing on somebody, and this is the small, reversible one — a
              person at a time, lifted with one press — sitting above the one
              that closes it on everybody. */}
      <Group title="Who is shut out">
        <Row
          label={BLOCKED_TITLE}
          value={blockedCount === null ? "—" : String(blockedCount)}
          onClick={() => setBlockedOpen(true)}
          ariaLabel={BLOCKED_TITLE}
        />
      </Group>
      {blockedOpen && <BlockedListDialog onClose={() => setBlockedOpen(false)} />}

      {/* 5. LAST, and last on purpose (owner, 2026-08-25). Taking the door
             down is the destructive act on this page, and a destructive act
             placed above the things somebody came here to edit is one they
             have to scroll past — repeatedly — to reach the bio. It goes at
             the bottom, where a person arrives only by deciding to.

             The two directions are deliberately NOT symmetrical: taking a
             live door down is a ceremony (four lines and the handle typed
             out, `takedown.tsx`); putting it back up is one button, because
             nobody regrets reopening. */}
      {settings !== null && (
        <Group>
          <div className={PLATE}>
            {paused ? (
              <>
                <SecondaryButton
                  type="button"
                  disabled={settingsBusy}
                  onClick={() => onSaveSettings({ status: "live" })}
                >
                  {REOPEN_LABEL}
                </SecondaryButton>
                <p className="mt-2 text-[13px] leading-snug text-fainter">{PAUSED_NOTE}</p>
              </>
            ) : (
              <TakedownControl handle={handle} busy={settingsBusy} onConfirm={onTakeDown} />
            )}
          </div>
        </Group>
      )}
    </>
  );
}

/**
 * WHAT GETS THROUGH, AS STRANGERS READ IT (ruling R-0910-10) — the one field on
 * this page whose words a stranger reads about the DOOR rather than about its
 * owner.
 *
 * It owns its own read-back, its own Save and its own draft, and it talks to
 * its own endpoint. That is not a second settings form by accident: the public
 * line is screened before it is written, exactly as the bio is, and the door's
 * settings PATCH screens nothing else — so the two writes are separate routes
 * and the two forms are separate forms rather than one form with a rule about
 * which of its fields go through a guard.
 *
 * THE BUTTON DRAFTS AND SAVES NOTHING, the same promise the bio's button makes
 * and for the same reason: Nob may put words in the box, never on the door.
 * What it reads is the owner's PRIVATE document — the Interests and Receipts
 * sections — and what it hands back is a translation of it for strangers, which
 * is the whole bridge this ruling needs. The rules themselves stay where they
 * are; nothing of them is ever published.
 */
export function PublicLineForm({
  handle,
  initial,
  canDraft,
  pastDue = false,
  onUpgrade,
}: {
  handle: string;
  initial: string | null;
  /** False on Free, where the door performs no AI work at all (ruling C10). */
  canDraft: boolean;
  /** The payment is failing: a refused draft says so and offers the card (ruling R-1006-3). */
  pastDue?: boolean;
  onUpgrade: () => void;
}) {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState<string | null>(initial);
  const [value, setValue] = useState(initial ?? "");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<{ text: string; failed: boolean; updateCard?: boolean } | null>(
    null,
  );
  const [ack, setAck] = useState(false);

  const dirty = value.trim() !== (saved ?? "");

  async function save(): Promise<void> {
    if (!dirty || busy) return;
    setBusy(true);
    setError(null);
    setNote(null);
    // An emptied box is `null` — the instruction "clear it", not an absence the
    // server should ignore. A cleared line means the plate shows no such block.
    const r = await updateDoorPublicLine(value.trim() ? value.trim() : null);
    setBusy(false);
    if (!r.ok) {
      setError(
        r.code === "BIO_TOO_LARGE"
          ? PUBLIC_LINE_TOO_LARGE_COPY
          : r.code === "INPUT_REFUSED"
            ? PUBLIC_LINE_REFUSED_COPY
            : PUBLIC_LINE_UNREACHABLE,
      );
      return;
    }
    setSaved(r.public_line);
    setValue(r.public_line ?? "");
    setAck(true);
    // The public card is what a stranger reads, and it just changed.
    void queryClient.invalidateQueries({ queryKey: qk.doorSettings() });
    void queryClient.invalidateQueries({ queryKey: qk.door(handle) });
  }

  async function askNob(): Promise<void> {
    if (drafting || busy || !canDraft) return;
    setDrafting(true);
    setNote(null);
    setError(null);
    const r = await draftPublicLine();
    setDrafting(false);
    if (!r.ok) {
      const wall = r.code === "PLAN_EXHAUSTED" && pastDue;
      setNote({
        text: wall
          ? PAST_DUE_WALL_LINE
          : r.code === "UNAUTHENTICATED"
            ? DRAFT_LINE_UNREACHABLE
            : (DRAFT_LINE_FAILED[r.code] ?? DRAFT_LINE_UNREACHABLE),
        failed: true,
        updateCard: wall,
      });
      return;
    }
    // Into the box, and NOT to the server: Save is still the owner's press.
    setValue(r.public_line);
    setNote({ text: AI_DRAFT_LABEL, failed: false });
  }

  return (
    <form
      className="grid gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <p className="block text-[12px] uppercase tracking-[0.12em] text-faint" id="door-line-label">
        {PUBLIC_LINE_LABEL}
      </p>
      <textarea
        id="door-public-line"
        rows={2}
        value={value}
        disabled={busy}
        aria-labelledby="door-line-label"
        aria-invalid={error !== null}
        aria-describedby={error ? "door-line-error" : "door-line-help"}
        onChange={(e) => {
          setValue(e.target.value);
          setAck(false);
        }}
        className={
          "block h-auto w-full resize-y rounded-lg border border-border bg-bg/60 px-3 py-2 " +
          "text-sm leading-relaxed text-text focus-visible:border-border-loud " +
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40 " +
          "disabled:opacity-50 aria-[invalid=true]:border-danger/60"
        }
      />
      {error ? (
        <p id="door-line-error" role="alert" className="text-[13px] text-danger">
          {error}
        </p>
      ) : (
        <p id="door-line-help" className="text-[13px] leading-relaxed text-fainter">
          {saved ? PUBLIC_LINE_HELP : `${PUBLIC_LINE_EMPTY} ${PUBLIC_LINE_HELP}`}
        </p>
      )}

      {/* The way out of the blank box. A quiet link rather than a button:
          writing your own line is the ordinary act, and this is the help
          offered beside it. */}
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <LinkButton
          type="button"
          disabled={!canDraft || drafting || busy}
          onClick={() => void askNob()}
        >
          {drafting ? DRAFTING_LINE_LABEL : DRAFT_LINE_LABEL}
        </LinkButton>
        {canDraft && !drafting && !note && (
          <span className="text-[12px] text-fainter">{DRAFT_LINE_HELP}</span>
        )}
        {note &&
          (note.failed ? (
            <>
              <span role="alert" className="text-[12px] text-danger">
                {note.text}
              </span>
              {note.updateCard && <UpdateCardButton className="h-7 px-2 text-xs" />}
            </>
          ) : (
            <span role="status" className="font-mono text-[11px] text-accent-ink/80">
              {note.text}
            </span>
          ))}
      </p>

      {/* The nudge names what is missing rather than what it costs (FR-9). */}
      {!canDraft && (
        <p className="flex flex-wrap items-baseline gap-x-2 text-[12px] text-fainter">
          <span>{DRAFT_LINE_LOCKED}</span>
          <LinkButton type="button" onClick={onUpgrade}>
            {DRAFT_LINE_UPGRADE}
          </LinkButton>
        </p>
      )}

      {/* Only while something has actually changed — a Save under a settled
          line is a button for a change nobody is making, and it is the same
          rule the bio's form keeps two plates up. */}
      {(dirty || ack) && (
        <p className="mt-1 flex items-center gap-3">
          <SecondaryButton type="submit" disabled={!dirty || busy}>
            {SAVE_LABEL}
          </SecondaryButton>
          {ack && (
            <span className="font-mono text-[11px] text-accent-ink/80" role="status">
              {SAVED_ACK}
            </span>
          )}
        </p>
      )}
    </form>
  );
}
